import {Component, OnInit, OnDestroy} from '@angular/core';
import * as _ from 'lodash';
import ItemMasterModel, {Attachments} from '../../../../../services/itemMaster.model';
import BusinessEntity from '../../../../../services/businessEntity.model';
import BusinessItemsModel from '../../../../../services/businessItems.model';
import ItemTypesModel from '../../../../../services/itemTypes.model';
import UOMMasterModel from '../../../../../services/uom.model';
import UOMConversionModel from '../../../../../services/uomConversion.model';
import CultureCategoryModel from '../../../../../services/cultureCategory.model';
import SpeciesTypeModel from '../../../../../services/speciesType.model';
import {SpeciesTypeService} from '../../../../../services/speciesType.service';
import {ItemTypesService} from '../../../../../services/itemTypes.service';
import {UomConversionService} from '../../../../../services/uomConversion.service';
import {CultureCategoryService} from '../../../../../services/cultureCategory.service';
import {BusinessEntityService} from '../../../../../services/businessEntity.service';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {UomService} from '../../../../../services/uom.service';
import {ItemMasterService} from '../../../../../services/itemMaster.service';
import {ShapesService} from '../../../../../services/shapes.service';
import ShapesModel from '../../../../../services/shapes.model';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * ProductsComponent
 *
 * This component is responsible for managing Product (Item Master) data within the Material Management module.
 * It provides a user interface for administrators, farmers, and farm managers to view, create, update, and search for products.
 * The component handles complex product configurations including UOM conversions, inventory settings, and categorization.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all products (Item Masters).
 * - Provide search functionality to filter products by name, type, or category.
 * - Manage the creation of new products with validation and file uploads.
 * - Handle updates to existing product details and configurations.
 * - Manage Unit of Measure (UOM) conversions for inventory items.
 * - Handle role-based access control for viewing and managing products.
 * - Support image uploads for product visualization.
 *
 * User Roles:
 * - Business Admin: Full access to manage products for their business.
 * - Farmer: Manage products for their farm operations.
 * - Farm Manager: Manage products for assigned farms.
 * - Provider: Manage product catalog for their services.
 *
 * Features:
 * - **CRUD Operations**: Create and Update products.
 * - **Real-time Search**: Filter list by multiple product attributes.
 * - **Infinite Scroll**: Load data in batches (implementation pending/partial).
 * - **Form Validation**: Ensures product details follow specific patterns and constraints.
 * - **UOM Conversion**: Configure conversions between different units of measure.
 * - **Image Upload**: Upload and display product images via S3.
 * - **Inventory Management**: Configure stockable items and reorder levels.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches reference data (Item Types, UOMs, Species, Culture Categories).
 * 3. Fetches products based on user role and business context.
 * 4. User interactions (search, add/edit, upload) trigger state updates and service calls.
 * 5. `addItemMaster` and `updateItemMaster` send data to backend and refresh the list on success.
 *
 * @component
 * @selector app-products
 * @example
 * <app-products></app-products>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss']
})
export class ProductsComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched item masters (products).
   * @type {Array<ItemMasterModel>}
   */
  public itemMasters: Array<ItemMasterModel>;

  /**
   * Model for the current item master being added or edited.
   * @type {ItemMasterModel}
   */
  public itemMaster: ItemMasterModel;

  /**
   * Array of business entities available for selection.
   * @type {Array<any>}
   */
  public businessEntities = new Array();

  /**
   * Currently selected business entity.
   * @type {BusinessEntity}
   */
  public businessEntity: BusinessEntity;

  /**
   * Array of business items (not actively used in current logic).
   * @type {Array<BusinessItemsModel>}
   */
  public businessItems: Array<BusinessItemsModel>;

  /**
   * Single business item model.
   * @type {BusinessItemsModel}
   */
  public businessItem: BusinessItemsModel;

  /**
   * Name of the business associated with the current operation.
   * @type {string}
   */
  public businessName: string;

  /**
   * Array of available item types (e.g., Feed, Medicine).
   * @type {Array<ItemTypesModel>}
   */
  public itemTypes: Array<ItemTypesModel>;

  /**
   * Array of all available Units of Measure (UOMs).
   * @type {Array<UOMMasterModel>}
   */
  public uoms: Array<UOMMasterModel>;

  /**
   * Array of system-defined UOMs.
   * @type {Array<UOMMasterModel>}
   */
  public systemuomdatatrue: Array<UOMMasterModel>;

  /**
   * Array of non-system-defined UOMs.
   * @type {Array<UOMMasterModel>}
   */
  public systemuomdatafalse: Array<UOMMasterModel>;

  /**
   * Selected UOM model.
   * @type {UOMMasterModel}
   */
  public uom: UOMMasterModel;

  /**
   * Array of UOM conversions.
   * @type {Array<UOMConversionModel>}
   */
  public UOMConversions: Array<UOMConversionModel>;

  /**
   * Specific UOM conversion model.
   * @type {UOMConversionModel}
   */
  public UOMConversion: UOMConversionModel;

  /**
   * Conversion model used for form binding.
   * @type {UOMConversionModel}
   */
  public conversion: UOMConversionModel;

  /**
   * Array of culture categories.
   * @type {Array<CultureCategoryModel>}
   */
  public cultureCategories: Array<CultureCategoryModel>;

  /**
   * Array of all species types.
   * @type {Array<SpeciesTypeModel>}
   */
  public speciesTypes: Array<SpeciesTypeModel>;

  /**
   * Filtered array of species types based on culture category.
   * @type {Array<SpeciesTypeModel>}
   */
  public dupSpeciesTypes: Array<SpeciesTypeModel>;

  /**
   * Original list of item masters before filtering.
   * @type {any[]}
   */
  public originalItemMasters: any[];

  /**
   * Array of available shapes for products (e.g., pellet shapes).
   * @type {Array<ShapesModel>}
   */
  public shapes: Array<ShapesModel>;

  /**
   * Attachment model for file uploads.
   * @type {Attachments}
   */
  public attachments: Attachments;

  /**
   * Flag to show/hide the submit button (for create mode).
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag to show/hide the update button (for edit mode).
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag indicating if the displayed data is recent/fresh.
   * @type {boolean}
   * @default false
   */
  public isRecentMasterData = false;

  /**
   * Flag indicating if a search has been executed.
   * @type {boolean}
   */
  public isSearchClicked: boolean;

  /**
   * Flag indicating if the form has been submitted.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Flag indicating active status (usage context dependent).
   * @type {boolean}
   */
  public isActive: boolean;

  /**
   * Current search query string.
   * @type {string}
   */
  public search: string;

  /**
   * Name of the logged-in user.
   * @type {string}
   */
  public loginUserName: string;

  /**
   * Business name of the logged-in user.
   * @type {string}
   */
  public loginBusinessName: string;

  /**
   * Business category of the logged-in user.
   * @type {string}
   */
  public loginUserBusinessCategory: string;

  /**
   * Business name associated with the logged-in user (duplicate/alias).
   * @type {string}
   */
  public loginUserBusinessName: string;

  /**
   * Role of the logged-in user.
   * @type {string}
   */
  public loginPersonType: string;

  /**
   * Name of the 'From' UOM in conversion.
   * @type {string}
   */
  public fromUOMName: string;

  /**
   * Name of the 'To' UOM in conversion.
   * @type {string}
   */
  public toUOMName: string;

  /**
   * Value for the 'From' unit in conversion (typically 1).
   * @type {number}
   */
  public conversionfromvalue: number;

  /**
   * Current form action ('ADD' or 'EDIT').
   * @type {string}
   */
  public formAction: string;

  /**
   * Flag indicating if the selected UOM is a system UOM.
   * @type {boolean}
   */
  public systemuomcondition: boolean;

  /**
   * Name of the selected UOM.
   * @type {string}
   */
  public UOMName: string;

  /**
   * Flag indicating if conversion logic is applicable (based on inventory status).
   * @type {boolean}
   */
  public conversionBoolean: boolean;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   */
  public responseLoader: boolean;

  // pagination sorting
  /**
   * Current page number for pagination.
   * @type {number}
   * @default 1
   */
  public currentPageNumber: number = 1;

  /**
   * Number of items per page.
   * @type {number}
   * @default 20
   */
  public noOfItemsPerPage: number = 20;

  /**
   * Flag indicating if it's the first time setting details.
   * @type {boolean}
   */
  public setFirsttime: boolean;

  /**
   * Flag to show/hide the conversion block in the form.
   * @type {boolean}
   */
  public conversionBlock: boolean;

  /**
   * Selected image file for upload.
   * @type {File | any}
   */
  public imageFile: File | any;

  /**
   * Name of the selected file.
   * @type {string}
   */
  public fileName: string;

  /**
   * Flag to show more details (usage context dependent).
   * @type {boolean}
   */
  public showMore: boolean;

  /**
   * Base64 string of the selected file for preview.
   * @type {string | ArrayBuffer}
   */
  public selectedbase64file: string | ArrayBuffer;

  /**
   * Flag indicating if a file has been selected.
   * @type {boolean}
   */
  public isFileSelected: boolean;

  /**
   * Flag indicating if the form/selection has been cleared.
   * @type {boolean}
   */
  public isCleared: boolean;

  /**
   * Catalogue for business admin (not actively used in current logic).
   * @type {any[]}
   */
  public businessAdminCatalogue: any[];

  /**
   * Application permissions object.
   * @type {any}
   */
  public appPermission: any;

  /**
   * Keyword used for filtering/searching.
   * @type {string}
   */
  public keyword: string;

  /**
   * Generic item property.
   * @type {any}
   */
  public item: any;

  /**
   * Flag indicating if no data is available.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag to prevent double clicks on add product action.
   * @type {boolean}
   */
  public addProductDoubleClick: boolean;

  /**
   * Array holding duplicated item masters (for display/filtering).
   * @type {Array<any>}
   */
  public dubItemmasters = new Array();

  /**
   * Active page number.
   * @type {number}
   */
  public activePageNo: number;

  /**
   * Flag to trigger clearing of search input.
   * @type {boolean}
   */
  public clearSearchInput: boolean;

  /**
   * Array of subscriptions for file downloads to manage cleanup.
   * @type {Subscription[]}
   * @default []
   */
  public downloadSubscriptions: Subscription[] = [];

  /**
   * Filtered array of products based on criteria.
   * @type {Array<any>}
   */
  public filteredProductArray = new Array();

  /**
   * Array for 'Your Catalogue' products (Provider context).
   * @type {Array<any>}
   */
  public yourCatalogueProductArray = new Array();

  /**
   * Array for 'Expense Catalogue' products (Provider context).
   * @type {Array<any>}
   */
  public expenseCatalogueProductArray = new Array();

  /**
   * Reactive form group for product details.
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Array holding API response data for item masters.
   * @type {Array<ItemMasterModel>}
   */
  public apidata: Array<ItemMasterModel>;

  /**
   * Filtered options for autocomplete/dropdowns.
   * @type {any[]}
   */
  filteredOptions: any[];

  /**
   * Count of fields in the form.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for ProductsComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * @constructor
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @service ActivatedRoute
   * @param {ShapesService} shapesService - Service for Shapes API operations.
   * @service ShapesService
   * @param {SpeciesTypeService} speciesTypeService - Service for Species Type API operations.
   * @service SpeciesTypeService
   * @param {ItemTypesService} itemTypesService - Service for Item Types API operations.
   * @service ItemTypesService
   * @param {UomConversionService} uomConversionService - Service for UOM Conversion API operations.
   * @service UomConversionService
   * @param {CultureCategoryService} cultureCategoryService - Service for Culture Category API operations.
   * @service CultureCategoryService
   * @param {UomService} uomService - Service for UOM API operations.
   * @service UomService
   * @param {ItemMasterService} itemMasterServe - Service for Item Master (Product) API operations.
   * @service ItemMasterService
   * @param {BusinessEntityService} businessEntityService - Service for Business Entity API operations.
   * @service BusinessEntityService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toasterService: ToastrService, private route: ActivatedRoute, public shapesService: ShapesService, public speciesTypeService: SpeciesTypeService, public itemTypesService: ItemTypesService, public uomConversionService: UomConversionService, public cultureCategoryService: CultureCategoryService, public uomService: UomService, public itemMasterServe: ItemMasterService, public businessEntityService: BusinessEntityService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.itemMaster = new ItemMasterModel();
    this.UOMConversion = new UOMConversionModel();
    this.conversion = new UOMConversionModel();
    this.attachments = new Attachments();
    this.registerForm = this.formBuilder.group({
      productname: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(30), Validators.pattern('^[a-zA-Z0-9 -]+$')]],
      subtype: ['', [Validators.minLength(2), Validators.maxLength(30), Validators.pattern('^[a-zA-Z0-9 -]+$')]],
      shape: [null],
      pelletsize: ['', [Validators.pattern('^[0-9]*$')]],
      usage: ['', [Validators.maxLength(30), Validators.pattern('^[a-zA-Z,.\\- ]*$')]],
      dosage: ['', [Validators.maxLength(30), Validators.pattern('^[a-zA-Z,.\\- ]*$')]],
      description: ['', [Validators.maxLength(200)]],
      upload: [null],
      itemtype: ['', [Validators.required]],
      culturecatrgory: [null],
      species: [null],
      packagetype: [null],
      stockable: [false],
      inventory: [null],
      conversion: [null],
      reordervalue: ['', [Validators.pattern('^[0-9]*$')]],
      fromuomid: [null],
      tovalue:  ['', [Validators.pattern('^[0-9]*$')]],
      touomid: [null],
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Tracks the current route.
   * 2. Retrieves user details from local storage.
   * 3. Sets up side navigation.
   * 4. Fetches reference data (Item Types, UOMs, Species, Culture Categories, Shapes).
   * 5. Loads products based on user role (Farmer vs Admin vs Provider).
   * 6. Loads business entities for selection.
   * 7. Initializes form state and flags.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.appPermission = this.route.snapshot.data['rolepermission'];
    this.showMore = false;
    try {
      /*users data*/
      // loginperson type details
      this.clearSearchInput = false;
      this.addProductDoubleClick = false;
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.loginUserName = localStorage.getItem('loginUserName');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      /*end users data*/
      this.sidenavaction.x_navigation();
      this.conversionfromvalue = 1;
      this.conversionBlock = false;
      this.submitButton = false;
      this.updateButton = false;
      this.isCleared = false;
      this.keyword = 'businessName';
      this.downloadSubscriptions = [];
      $('#imageUpload').val('');
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.responseLoader = true;
      }
      this.itemTypes = await this.itemTypesService.getAllItemTypes();
      this.itemTypes = _.orderBy(this.itemTypes, ['id'], ['asc']);
      this.uoms = await this.uomService.getAllUom();
      this.speciesTypes = await this.speciesTypeService.getAllSpeciesType();
      this.dupSpeciesTypes = new Array();
      this.systemuomdatatrue = this.uoms.filter(uom => uom.system_uom == true);
      this.systemuomdatafalse = this.uoms.filter(uom => uom.system_uom == false);
      this.cultureCategories = await this.cultureCategoryService.getAllCultureCategories();
      this.cultureCategories = this.cultureCategories.filter(culture => culture.name != 'Hatchery');
      this.formAction = 'ADD';
      this.isSearchClicked = false;
      this.conversion.from_units_uom_id = undefined;
      this.conversion.to_units_uom_id = undefined;
      this.shapes = await this.shapesService.getAllShapes();
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.itemMaster.business_id = localStorage.businessID;
        this.businessName = this.loginUserBusinessName;
        this.getAllItemMasterbyloginFarmer();
      }
      let allBusiness = await this.businessEntityService.getAllBusiness();
      let business = allBusiness.filter(businessEntity => (businessEntity && businessEntity.businessType[0] && businessEntity.businessType[0].businessTypeID != '618a18ac0f339c001e991b08') && (businessEntity && businessEntity.businessType[0] && businessEntity.businessType[0].businessTypeID != '62b3fa8998be51001d17a310'));
      for (let j = 0; j < business.length; j++) {
        let each = business[j];
        if (each.providerID) {
          if ((each.providerID.length == 0)) {
            this.businessEntities.push(each);
          }
        }
      }
      this.filteredOptions = this.businessEntities;
      this.updateToggleSwitchState();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  public get f() {
    return this.registerForm.controls;
  }

  // Function to handle card hover (show card actions)
  /**
   * Handles card hover event to show actions.
   * Adds a CSS class to the card element.
   *
   * @method showCardActions
   * @param {Event} event - The hover event.
   * @returns {void}
   */
  showCardActions(event: Event) {
    const card = (event.target as HTMLElement).closest('.card');
    if (card) {
      card.classList.add('d-card-show');
    }
  }

  // Function to handle card hover out (hide card actions)
  /**
   * Handles card hover out event to hide actions.
   * Removes a CSS class from the card element.
   *
   * @method hideCardActions
   * @param {Event} event - The hover out event.
   * @returns {void}
   */
  hideCardActions(event: Event) {
    const card = (event.target as HTMLElement).closest('.card');
    if (card) {
      card.classList.remove('d-card-show');
    }
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Unsubscribes from image download streams.
   * - Cancels ongoing HTTP requests for all injected services.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.unsubscribeAllDownloadImageStreams();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.itemTypesService.cancelOnGoingHttpRequests();
    this.uomService.cancelOnGoingHttpRequests();
    this.speciesTypeService.cancelOnGoingHttpRequests();
    this.cultureCategoryService.cancelOnGoingHttpRequests();
    this.shapesService.cancelOnGoingHttpRequests();
    this.itemMasterServe.cancelOnGoingHttpRequests();
  }

  //To cancel image downloads from the s3 bucket
  /**
   * Unsubscribes from all active image download subscriptions.
   *
   * @method unsubscribeAllDownloadImageStreams
   * @returns {void}
   */
  public unsubscribeAllDownloadImageStreams() {
    if (this.downloadSubscriptions.length > 0) {
      this.downloadSubscriptions.forEach((request) => {
        if (request != undefined) {
          request.unsubscribe();
        }
      });
      this.downloadSubscriptions = [];
    }
  }

  // get all item master details based on admin
  /**
   * Fetches all item masters (products) for system admin view.
   *
   * - Calls `getAllItemMaster` service method.
   * - Downloads images for each item from S3.
   * - Filters products by item type for display.
   *
   * @async
   * @method getAllItemMaster
   * @returns {Promise<void>}
   */
  public async getAllItemMaster() {
    this.isSearchClicked = false;
    this.responseLoader = true;
    this.businessName = 'system items';
    this.downloadSubscriptions = [];
    try {
      this.apidata = await this.itemMasterServe.getAllItemMaster();
      if (this.apidata && this.apidata.length == 0) {
        this.toasterService.clear();
        this.toasterService.warning('Data not available');
        this.responseLoader = false;
      }
      this.originalItemMasters = JSON.parse(JSON.stringify(this.apidata));
      this.dubItemmasters = JSON.parse(JSON.stringify(this.apidata));
      this.filteredProductArray = [];
      let downloadSubscription: Subscription;
      for (let i = 0; i < this.originalItemMasters.length; i++) {
        if (this.originalItemMasters[i].attachment_path != null) {
          downloadSubscription = this.itemMasterServe.downloadFileFromS3Bucket(this.originalItemMasters[i].attachment_path).subscribe(fileBlob => {
            const readFile = new FileReader();
            readFile.readAsDataURL(fileBlob);
            readFile.onload = () => {
              this.dubItemmasters[i].attachment_path = readFile.result;
              this.originalItemMasters[i].attachment_path = readFile.result;
            };
          }, () => {
            this.dubItemmasters[i].attachment_path = null;
            this.originalItemMasters[i].attachment_path = null;
          });
        }
        this.downloadSubscriptions.push(downloadSubscription);
      }
      if (this.itemTypes && this.itemTypes.length > 0) {
        for (let i = 0; i < this.itemTypes.length; i++) {
          let itemMaster = this.originalItemMasters.filter(item => item && item.ItemType && item.ItemType.name === this.itemTypes[i].name);
          this.filteredProductArray.push(itemMaster);
        }
      }
      this.responseLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  // get all item master details based on farmer
  /**
   * Fetches item masters for a specific farmer business.
   *
   * - Calls `getAllItemMasterbyFarmer` service method with business ID.
   * - Downloads images for each item from S3.
   * - Filters products by item type for display.
   *
   * @async
   * @method getAllItemMasterbyFarmer
   * @returns {Promise<void>}
   */
  public async getAllItemMasterbyFarmer() {
    try {
      this.isSearchClicked = false;
      this.responseLoader = true;
      this.downloadSubscriptions = [];
      this.apidata = await this.itemMasterServe.getAllItemMasterbyFarmer(this.itemMaster.business_id);
      if (this.apidata && this.apidata.length == 0) {
        this.toasterService.clear();
        this.toasterService.warning('Data not available');
        this.responseLoader = false;
      }
      this.originalItemMasters = JSON.parse(JSON.stringify(this.apidata));
      this.dubItemmasters = JSON.parse(JSON.stringify(this.apidata));
      this.filteredProductArray = [];
      let downloadSubscription: Subscription;
      for (let i = 0; i < this.originalItemMasters.length; i++) {
        if (this.originalItemMasters[i].attachment_path != null) {
          downloadSubscription = this.itemMasterServe.downloadFileFromS3Bucket(this.originalItemMasters[i].attachment_path).subscribe(fileBlob => {
            const readFile = new FileReader();
            readFile.readAsDataURL(fileBlob);
            readFile.onload = () => {
              this.dubItemmasters[i].attachment_path = readFile.result;
              this.originalItemMasters[i].attachment_path = readFile.result;
            };
          }, () => {
            this.dubItemmasters[i].attachment_path = null;
            this.originalItemMasters[i].attachment_path = null;
          });
        }
        this.downloadSubscriptions.push(downloadSubscription);
      }
      if (this.itemTypes && this.itemTypes.length > 0) {
        for (let i = 0; i < this.itemTypes.length; i++) {
          let itemMaster = this.originalItemMasters.filter(item => item && item.ItemType && item.ItemType.name === this.itemTypes[i].name);
          this.filteredProductArray.push(itemMaster);
        }
      }
      this.responseLoader = false;
      this.isRecentMasterData = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  // get all item master details based on farmer
  /**
   * Fetches item masters based on the logged-in user's role and business.
   *
   * - Handles logic for Farmer, Farm Manager, Business Admin (Farm & Provider).
   * - For Providers, separates products into 'Your Catalogue' and 'Expense Catalogue'.
   * - Downloads images for items.
   * - Filters products by item type.
   *
   * @async
   * @method getAllItemMasterbyloginFarmer
   * @returns {Promise<void>}
   */
  public async getAllItemMasterbyloginFarmer() {
    try {
      this.isSearchClicked = false;
      this.responseLoader = true;
      this.itemMasters = new Array();
      this.originalItemMasters = new Array();
      this.dubItemmasters = new Array();
      this.downloadSubscriptions = [];
      this.expenseCatalogueProductArray = new Array();
      if (['Farmer', 'Farm Manager', 'Business Admin'].includes(this.loginPersonType) && this.loginUserBusinessCategory == 'Farm') {
        this.apidata = await this.itemMasterServe.getAllItemMasterbyFarmer(localStorage.businessID);
        this.originalItemMasters = JSON.parse(JSON.stringify(this.apidata));
        this.dubItemmasters = JSON.parse(JSON.stringify(this.apidata));
        this.filteredProductArray = [];
        let downloadSubscription: Subscription;
        for (let i = 0; i < this.originalItemMasters.length; i++) {
          if (this.originalItemMasters[i].attachment_path != null) {
            downloadSubscription = this.itemMasterServe.downloadFileFromS3Bucket(this.originalItemMasters[i].attachment_path).subscribe(fileBlob => {
              const readFile = new FileReader();
              readFile.readAsDataURL(fileBlob);
              readFile.onload = () => {
                this.dubItemmasters[i].attachment_path = readFile.result;
                this.originalItemMasters[i].attachment_path = readFile.result;
              };
            }, () => {
              this.dubItemmasters[i].attachment_path = null;
              this.originalItemMasters[i].attachment_path = null;
            });
          }
          this.downloadSubscriptions.push(downloadSubscription);
        }
        if (this.itemTypes && this.itemTypes.length > 0) {
          for (let i = 0; i < this.itemTypes.length; i++) {
            let itemMaster = this.originalItemMasters.filter(item => item && item.ItemType && item.ItemType.name === this.itemTypes[i].name);
            this.filteredProductArray.push(itemMaster);
          }
        }
        this.responseLoader = false;
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        let item = await this.itemMasterServe.getAllItemMasterbyFarmer(localStorage.businessID);
        this.apidata = [];
        for (let k = 0; k < item.length; k++) {
          let each = item[k];
          if ((each.item_type_id == 1 || each.item_type_id == 2) && each.system == false) {
            this.apidata.push(each);
          } else if ((each.item_type_id != 1 && each.item_type_id != 2) && each.system == true) {
            this.apidata.push(each);
          }
        }
        this.originalItemMasters = JSON.parse(JSON.stringify(this.apidata));
        this.dubItemmasters = JSON.parse(JSON.stringify(this.apidata));
        this.yourCatalogueProductArray = [];
        this.expenseCatalogueProductArray = [];
        let downloadSubscription: Subscription;
        for (let i = 0; i < this.originalItemMasters.length; i++) {
          if (this.originalItemMasters[i].attachment_path != null) {
            downloadSubscription = this.itemMasterServe.downloadFileFromS3Bucket(this.originalItemMasters[i].attachment_path).subscribe(fileBlob => {
              const readFile = new FileReader();
              readFile.readAsDataURL(fileBlob);
              readFile.onload = () => {
                this.dubItemmasters[i].attachment_path = readFile.result;
                this.originalItemMasters[i].attachment_path = readFile.result;
              };
            }, () => {
              this.dubItemmasters[i].attachment_path = null;
              this.originalItemMasters[i].attachment_path = null;
            });
          }
          this.downloadSubscriptions.push(downloadSubscription);
        }
        if (this.itemTypes && this.itemTypes.length > 0) {
          let feedtreatmentItems = this.itemTypes.filter(type => type.name == 'Feed' || type.name == 'Treatment');
          for (let i = 0; i < feedtreatmentItems.length; i++) {
            let itemMaster = this.originalItemMasters.filter(item => item && item.ItemType && item.ItemType.name === feedtreatmentItems[i].name);
            this.yourCatalogueProductArray.push(itemMaster);
          }
        }
        if (this.itemTypes && this.itemTypes.length > 0) {
          let feedtreatmentItems = this.itemTypes.filter(type => type.name != 'Feed' && type.name != 'Treatment');
          for (let i = 0; i < feedtreatmentItems.length; i++) {
            let itemMaster = this.originalItemMasters.filter(item => item && item.ItemType && item.ItemType.name === feedtreatmentItems[i].name);
            this.expenseCatalogueProductArray.push(itemMaster);
          }
        }
        this.responseLoader = false;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  // if select system type is yes
  /**
   * Displays system-defined item masters when 'System' toggle is selected.
   *
   * - Clears current list.
   * - Calls `getAllItemMaster` if conditions are met.
   *
   * @async
   * @method displaytabledata
   * @returns {Promise<void>}
   */
  public async displaytabledata() {
    try {
      this.unsubscribeAllDownloadImageStreams();
      this.isRecentMasterData = false;
      this.isSearchClicked = false;
      this.itemMasters = new Array();
      this.originalItemMasters = new Array();
      this.dubItemmasters = new Array();
      this.itemMaster.business_id = '';
      this.item = undefined;
      this.NoDataMessage = false;
      this.search = '';
      if (this.itemMaster.system == true && this.itemMaster.business_id == '') {
        await this.getAllItemMaster();
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  // if select the system type is no and farm select
  /**
   * Displays farm-specific item masters when 'System' toggle is deselected and a farm is chosen.
   *
   * - Clears current list.
   * - Calls `getAllItemMasterbyFarmer` if conditions are met.
   *
   * @async
   * @method gettabledatabyfarm
   * @returns {Promise<void>}
   */
  public async gettabledatabyfarm() {
    try {
      this.unsubscribeAllDownloadImageStreams();
      this.isSearchClicked = false;
      this.isRecentMasterData = false;
      this.itemMasters = new Array();
      this.originalItemMasters = new Array();
      this.dubItemmasters = new Array();
      if (this.itemMaster.system == false && this.itemMaster.business_id != '') {
        await this.getAllItemMasterbyFarmer();
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /*get species types by culture category id*/
  /**
   * Filters species types based on the selected culture category ID.
   *
   * @async
   * @method getSpeciesTypeByCultureCategoryId
   * @param {number} categoryId - The ID of the culture category.
   * @returns {Promise<void>}
   */
  public async getSpeciesTypeByCultureCategoryId(categoryId: number) {
    try {
      this.dupSpeciesTypes = new Array();
      for (let i = 0; i < this.speciesTypes.length; i++) {
        if (this.speciesTypes[i].culture_category_id == categoryId) {
          this.dupSpeciesTypes.push(this.speciesTypes[i]);
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Retrieves product profile by ID for viewing/editing.
   *
   * @async
   * @method getProductProfileById
   * @param {string} id - The ID of the product.
   * @returns {Promise<void>}
   */
  public async getProductProfileById(id: string) {
    this.updateButton = true;
    this.addProductDoubleClick = false;
    this.itemMaster = this.itemMasters.find(product => product && product.id === id);
  }

  /**
   * Populates the form with details of the selected item master for editing.
   *
   * - Sets form values, flags, and conversion details.
   *
   * @async
   * @method setItemMasterDetails
   * @param {ItemMasterModel} currData - The item master object to edit.
   * @returns {Promise<void>}
   */
  public async setItemMasterDetails(currData: ItemMasterModel) {
    this.showForm = true;
    this.addProductDoubleClick = false;
    this.setFirsttime = true;
    this.updateButton = true;
    this.submitButton = false;
    this.isFileSelected = false;
    this.isCleared = false;
    if (currData.isInventory == true) {
      this.conversionBlock = true;
    }
    this.conversionBoolean = currData.isInventory;
    this.itemMaster.id = currData.id;
    this.itemMaster.sub_type = currData.sub_type;
    this.itemMaster.pellet_size = currData.pellet_size;
    this.itemMaster.usage = currData.usage;
    this.itemMaster.dosage = currData.dosage;
    this.itemMaster.shape_id = currData.shape_id;
    this.itemMaster.species_type_id = currData.species_type_id;
    this.conversion.item_id = currData && currData.UOMConversion[0] && currData.UOMConversion[0].item_id;
    this.conversion.id = currData && currData.UOMConversion[0] && currData.UOMConversion[0].id;
    this.itemMaster.isInventory = currData.isInventory;
    this.itemMaster.item_type_id = currData.item_type_id;
    this.itemMaster.culture_category_id = currData.culture_category_id;
    this.itemMaster.uom_id = currData.uom_id;
    this.itemMaster.item_name = currData.item_name;
    this.itemMaster.stockable = currData.stockable;
    this.itemMaster.isInventory = currData.isInventory;
    this.itemMaster.description = (currData && currData.description == null || currData && currData.description == '') ? '--' : currData && currData.description;
    this.conversion.from_units_uom_id = currData && currData.UOMConversion[0] && currData.UOMConversion[0].from_units_uom_id;
    this.conversion.to_value = currData && currData.UOMConversion[0] && currData.UOMConversion[0].to_value;
    this.conversion.to_units_uom_id = currData && currData.UOMConversion[0] && currData.UOMConversion[0].to_units_uom_id;
    this.itemMaster.reorder_level_value = currData.reorder_level_value;
    this.itemMaster.reorder_uom_id = currData.reorder_uom_id;
    // this.UOMName = currData.ReorderUOM.name;
    this.onchangeUOM('from'); // from get uom name
    this.onchangeUOM('to'); // from get uom name
    this.onchangeUOM('tablefrom');
    this.itemMaster.species_type_id = currData.species_type_id;
    this.getSpeciesTypeByCultureCategoryId(this.itemMaster.culture_category_id);
    this.toUOMName = currData && currData.UOMConversion[0] && currData.UOMConversion[0].ToUOM.name;
    this.fromUOMName = currData && currData.UOMConversion[0] && currData.UOMConversion[0].FromUOM.name;
    this.selectedbase64file = currData.attachment_path ? currData.attachment_path : null;
    if (currData.attachment_path) {
      let item = this.apidata.filter(eachitem => eachitem && eachitem.id == currData.id);
      this.itemMaster.attachment_path = item[0].attachment_path;
    }
    this.updateToggleSwitchState();
  } // End click on display particular item master details

  // Add item master service
  /**
   * Handles the creation of a new item master (product).
   *
   * - Validates the form and business logic.
   * - Handles file upload to S3 if an image is selected.
   * - Calls `addItemMaster` service method.
   * - Refreshes the list on success.
   *
   * @async
   * @method addItemMaster
   * @returns {Promise<void | boolean>}
   */
  public async addItemMaster() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    try {
      $('tr').removeClass('rowactive');
      if ((this.itemMaster.system == undefined || this.itemMaster.system == null) && (this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Business Admin')) {
        this.toasterService.clear();
        this.toasterService.error('Please select system item');
        return false;
      }
      if ((this.itemMaster.system == false && this.itemMaster.business_id == '') && (this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Business Admin')) {
        this.toasterService.clear();
        this.toasterService.error('Please select business ');
        return false;
      }
      if ((this.itemMaster.item_type_id == 1 || this.itemMaster.item_type_id == 2) && this.itemMaster.culture_category_id == undefined) {
        this.toasterService.clear();
        this.toasterService.error('Please select culture category', '', {
          timeOut: 2000
        });
        return false;
      }
      if ((this.itemMaster.item_type_id == 1 || this.itemMaster.item_type_id == 2) && this.itemMaster.species_type_id == undefined) {
        this.toasterService.clear();
        this.toasterService.error('Please select species', '', {
          timeOut: 2000
        });
        return false;
      }
      if (this.itemMaster.uom_id == undefined) {
        this.toasterService.error('Please select package type', '', {
          timeOut: 2000
        });
        return false;
      }
      if (this.itemMaster.stockable == true && this.itemMaster.uom_id == undefined) {
        this.toasterService.clear();
        this.toasterService.error('Please select package type', '', {
          timeOut: 2000
        });
        return false;
      }
      if (this.itemMaster.stockable == undefined) {
        /*this.toasterService.error('Please select stockable', '', {
          timeOut: 2000
        });
        return false;*/
        this.itemMaster.stockable = false;
        this.itemMaster.isInventory = false;
      }
      if (this.itemMaster.stockable == true && (this.conversion.from_units_uom_id == null || this.conversion.from_units_uom_id == undefined) && this.itemMaster.isInventory == true) {
        this.toasterService.clear();
        this.toasterService.error('Please select FROM UOM value', '', {
          timeOut: 2000
        });
        this.onclickconversionopen();
        return false;
      }
      if (this.itemMaster.stockable == true && (this.conversion.to_value == 0 || this.conversion.to_value == undefined) && this.itemMaster.isInventory == true) {
        this.toasterService.clear();
        this.toasterService.error('Please enter TO value', '', {
          timeOut: 2000
        });
        this.onclickconversionopen();
        return false;
      }
      if (this.itemMaster.stockable == true && !this.conversion.to_units_uom_id && this.itemMaster.isInventory == true) {
        this.toasterService.clear();
        this.toasterService.error('Please select to uom value', '', {
          timeOut: 2000
        });
        this.onclickconversionopen();
        return false;
      }
      if (this.itemMaster.stockable == true && this.itemMaster.reorder_level_value == undefined && this.itemMaster.isInventory == true) {
        this.toasterService.clear();
        this.toasterService.error('Please enter reorder value', '', {
          timeOut: 2000
        });
        return false;
      }
      this.addProductDoubleClick = true;
      this.itemMaster.created_name = this.loginUserName;
      this.itemMaster.updated_name = this.loginUserName;
      this.itemMaster.created_by = localStorage.personID;
      this.itemMaster.created_by = localStorage.personID;
      this.itemMaster.updated_date = this.todayDate();
      this.itemMaster.created_date = this.todayDate();
      this.unsubscribeAllDownloadImageStreams();
      if ((this.loginPersonType != 'Business Admin' && this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginUserBusinessCategory != 'Farm')) {
        this.itemMaster.system = this.itemMaster.system;
        if (this.itemMaster.system == false) {
          this.itemMaster.business_id = this.itemMaster.business_id;
        } else {
          this.itemMaster.business_id = '';
        }
      } else {
        this.itemMaster.business_id = localStorage.businessID;
        this.businessName = this.loginUserBusinessName;
      }
      if (this.itemMaster.isInventory) {
        this.itemMaster.conversion = {
          from_value: 1,
          to_value: Number(this.conversion.to_value),
          to_units_uom_id: Number(this.conversion.to_units_uom_id),
          system_defined: false,
          createdAt: new Date(),
          updatedAt: new Date(),
          id: '',
          from_units_uom_id: Number(this.conversion.from_units_uom_id),
        };
        this.itemMaster.reorder_uom_id = Number(this.itemMaster.uom_id);
      } else {
        this.itemMaster.conversion = undefined;
        this.itemMaster.UOMConversion = [];
      }
      /*find business name by selected businessid*/
      if (this.loginPersonType != 'Business Admin' && this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginUserBusinessCategory != 'Farm') {
        if (this.itemMaster.business_id) {
          const selectedBusinessObj = _.filter(this.businessEntities, (o) => {
            return o.id == this.itemMaster.business_id;
          });
          if (selectedBusinessObj.length > 0) {
            this.attachments.businessName = selectedBusinessObj[0].businessName.replace(/ /g, '_');
          }
        }
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        this.attachments.businessName = this.loginUserBusinessName;
      }
      if (!this.isFileSelected && this.isCleared) {
        this.itemMaster.attachment_path = null;
      }
      let itemmaster = this.itemMaster;
      delete itemmaster.id;
      delete this.itemMaster.id;
      if (this.isFileSelected) {
        const upload = this.itemMasterServe.uploadFileToS3Bucket(this.imageFile, this.businessName, 'Products', `IM-${this.imageFile.name}`);
        upload.subscribe(async (response) => {
          if (response) {
            itemmaster.attachment_path = Object.values(response)[0];
            let itemMasteradd = await this.itemMasterServe.addItemMaster(itemmaster);
            if (itemMasteradd) {
              this.toasterService.success('Item master created successfully', '', {
                timeOut: 3000
              });
              await this.handlesucessData();
              this.registerForm.markAsPristine(); // optional - clears dirty flags
              this.registerForm.markAsUntouched(); // optional - clears touched state
              this.itemMaster.id = undefined;
              this.submitted = false;
              this.itemMaster.stockable = undefined;
              this.itemMaster.isInventory = undefined;
              this.itemMaster.item_type_id = undefined;
              this.itemMaster.culture_category_id = undefined;
              this.itemMaster.item_name = '';
              this.itemMaster.description = '';
              this.itemMaster.uom_id = undefined;
              this.conversion.to_value = undefined;
              this.conversion.to_units_uom_id = undefined;
              this.itemMaster.reorder_level_value = undefined;
              this.itemMaster.reorder_uom_id = undefined;
              this.conversion.from_value = undefined;
              this.conversion.system_defined = undefined;
              this.conversion.id = undefined;
              this.conversion.from_units_uom_id = undefined;
              this.itemMaster.sub_type = undefined;
              this.itemMaster.shape_id = undefined;
              this.itemMaster.pellet_size = undefined;
              this.itemMaster.usage = undefined;
              this.itemMaster.dosage = undefined;
              this.itemMaster.attachment = {};
              this.conversionBlock = false;
              this.conversion.to_units_uom_id = undefined;
              this.conversion.from_units_uom_id = undefined;
              this.submitted = false;
              this.imageFile = undefined;
              this.isFileSelected = false;
              this.fileName = undefined;
              this.selectedbase64file = undefined;
              this.isCleared = false;
              this.itemMaster.attachment_path = null;
              this.itemMaster.conversion = undefined;
              this.itemMaster.UOMConversion = [];
              this.showForm = false;
              this.search = '';
            }
          } else {
            this.toasterService.clear();
            this.toasterService.error('File upload failed--', '', {
              timeOut: 2000
            });
          }
        });
      } else {
        let itemMasterAdded = await this.itemMasterServe.addItemMaster(this.itemMaster);
        if (itemMasterAdded) {
          this.toasterService.success('Item master created successfully', '', {
            timeOut: 2000
          });
          await this.handlesucessData();
          this.itemMaster.id = undefined;
          this.registerForm.markAsPristine(); // optional - clears dirty flags
          this.registerForm.markAsUntouched(); // optional - clears touched state
          this.itemMaster.stockable = undefined;
          this.itemMaster.isInventory = undefined;
          this.submitted = false;
          this.itemMaster.item_type_id = undefined;
          this.itemMaster.culture_category_id = undefined;
          this.itemMaster.item_name = '';
          this.itemMaster.description = '';
          this.itemMaster.uom_id = undefined;
          this.conversion.to_value = undefined;
          this.conversion.to_units_uom_id = undefined;
          this.itemMaster.reorder_level_value = undefined;
          this.itemMaster.reorder_uom_id = undefined;
          this.conversion.from_value = undefined;
          this.conversion.system_defined = undefined;
          this.conversion.id = undefined;
          this.conversion.from_units_uom_id = undefined;
          this.itemMaster.sub_type = undefined;
          this.itemMaster.shape_id = undefined;
          this.itemMaster.pellet_size = undefined;
          this.itemMaster.usage = undefined;
          this.itemMaster.dosage = undefined;
          this.itemMaster.attachment = {};
          this.conversionBlock = false;
          this.conversion.to_units_uom_id = undefined;
          this.conversion.from_units_uom_id = undefined;
          this.submitted = false;
          this.imageFile = undefined;
          this.isFileSelected = false;
          this.fileName = undefined;
          this.selectedbase64file = undefined;
          this.isCleared = false;
          this.itemMaster.attachment_path = null;
          this.itemMaster.conversion = undefined;
          this.itemMaster.UOMConversion = [];
          this.showForm = false;
          this.search = '';
        }
      }
      this.addProductDoubleClick = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.addProductDoubleClick = false;
    }
  }

// update item master
  /**
   * Handles the update of an existing item master (product).
   *
   * - Validates the form and business logic.
   * - Handles file upload to S3 if a new image is selected.
   * - Calls `updateItemMaster` service method.
   * - Refreshes the list on success.
   *
   * @async
   * @method updateItemMaster
   * @returns {Promise<void | boolean>}
   */
  public async updateItemMaster() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    try {
      if (this.itemMaster.item_type_id == undefined) {
        this.toasterService.clear();
        this.toasterService.error('Please select item type', '', {
          timeOut: 2000
        });
        return false;
      }
      if ([1, 2].includes(this.itemMaster.item_type_id) && this.itemMaster.culture_category_id == undefined) {
        this.toasterService.clear();
        this.toasterService.error('Please select culture category', '', {
          timeOut: 2000
        });
        return false;
      }
      if ([1, 2].includes(this.itemMaster.item_type_id) && this.itemMaster.species_type_id == undefined) {
        this.toasterService.clear();
        this.toasterService.error('Please select species', '', {
          timeOut: 2000
        });
        return false;
      }
      if (!this.itemMaster.item_name) {
        this.toasterService.error('Please enter item name', '', {
          timeOut: 2000
        });
        return false;
      }
      if (this.itemMaster.stockable == true && this.itemMaster.uom_id == undefined) {
        this.toasterService.error('Please select UOM', '', {
          timeOut: 2000
        });
        return false;
      }
      if (this.itemMaster.stockable == undefined) {
        this.toasterService.error('Please select stockable', '', {
          timeOut: 2000
        });
        return false;
      }
      if ([1, 2].includes(this.itemMaster.item_type_id) && this.itemMaster.isInventory == false && this.itemMaster.stockable == false && (this.conversion.from_units_uom_id == undefined || this.conversion.from_units_uom_id == null)) {
        this.toasterService.error('Please select FROM UOM value', '', {
          timeOut: 2000
        });
        this.onclickconversionopen();
        return false;
      }
      if (this.itemMaster.stockable == true && (!this.conversion.to_value) && this.itemMaster.isInventory == true) {
        this.toasterService.error('Please enter TO value', '', {
          timeOut: 2000
        });
        this.onclickconversionopen();
        return false;
      }
      if ([1, 2].includes(this.itemMaster.item_type_id) && this.itemMaster.isInventory == false && this.itemMaster.stockable == false && (this.conversion.to_units_uom_id == undefined || this.conversion.to_units_uom_id == null)) {
        this.toasterService.error('Please select TO UOM value', '', {
          timeOut: 2000
        });
        this.onclickconversionopen();
        return false;
      }
      if (this.itemMaster.stockable == true && this.itemMaster.reorder_level_value == undefined && this.itemMaster.isInventory == true) {
        this.toasterService.error('Please enter reorder value', '', {
          timeOut: 2000
        });
        return false;
      }
      this.addProductDoubleClick = true;
      this.unsubscribeAllDownloadImageStreams();
      if (this.loginPersonType != 'Business Admin' && this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager') {
        this.itemMaster.system = this.itemMaster.system;
        if (this.itemMaster.system == false) {
          this.itemMaster.business_id = this.itemMaster.business_id;
        } else {
          this.itemMaster.business_id = '';
          this.itemMaster.business_id = this.itemMaster.business_id;
        }
      } else {
        this.itemMaster.business_id = localStorage.businessID;
        this.businessName = this.loginBusinessName;
      }
      if (this.itemMaster.isInventory) {
        this.itemMaster.conversion = {
          from_value: 1,
          to_value: Number(this.conversion.to_value),
          to_units_uom_id: Number(this.conversion.to_units_uom_id),
          system_defined: false,
          createdAt: new Date(),
          updatedAt: new Date(),
          id: this.conversion.id,
          from_units_uom_id: Number(this.conversion.from_units_uom_id),
          item_id: Number(this.itemMaster.id)
        };
        this.itemMaster.reorder_uom_id = this.itemMaster.uom_id;
      } else if (this.itemMaster.isInventory == false) {
        this.itemMaster.reorder_level_value = null;
        this.itemMaster.reorder_uom_id = null;
      } else {
        this.conversion.item_id = Number(this.itemMaster.id);
      }
      if (!this.isFileSelected && this.isCleared) {
        this.itemMaster.attachment_path = null;
      }
      let itemMaster = this.itemMaster;
      if (this.isFileSelected) {
        const upload = this.itemMasterServe.uploadFileToS3Bucket(this.imageFile, this.businessName, 'Products', `IM-${this.imageFile.name}`);
        upload.subscribe(async (response) => {
          if (response) {
            itemMaster.attachment_path = Object.values(response)[0];
            let itemMasterUpdated = await this.itemMasterServe.updateItemMaster(itemMaster);
            if (itemMasterUpdated) {
              this.toasterService.success('Item master Updated successfully', '', {
                timeOut: 2000
              });
              await this.handlesucessData();
              this.registerForm.markAsPristine(); // optional - clears dirty flags
              this.registerForm.markAsUntouched(); // optional - clears touched state
              this.showForm = false;
              this.UOMConversion = new UOMConversionModel();
              this.conversion = new UOMConversionModel();
              this.itemMaster.conversion = undefined;
              this.itemMaster.stockable = undefined;
              this.itemMaster.isInventory = undefined;
              this.itemMaster.item_type_id = undefined;
              this.itemMaster.culture_category_id = undefined;
              this.itemMaster.item_name = '';
              this.itemMaster.description = '';
              this.itemMaster.uom_id = undefined;
              this.conversion.to_value = undefined;
              this.conversion.to_units_uom_id = undefined;
              this.itemMaster.reorder_level_value = undefined;
              this.itemMaster.reorder_uom_id = undefined;
              this.conversion.from_value = undefined;
              this.conversion.system_defined = undefined;
              this.conversion.id = undefined;
              this.conversion.from_units_uom_id = undefined;
              this.itemMaster.sub_type = undefined;
              this.itemMaster.shape_id = undefined;
              this.itemMaster.pellet_size = undefined;
              this.itemMaster.usage = undefined;
              this.itemMaster.dosage = undefined;
              this.itemMaster.attachment = {};
              this.conversionBlock = false;
              this.conversion.to_units_uom_id = undefined;
              this.conversion.from_units_uom_id = undefined;
              this.submitted = false;
              this.imageFile = undefined;
              this.isFileSelected = false;
              this.selectedbase64file = undefined;
              this.fileName = undefined;
              this.itemMaster.attachment_path = null;
              this.isCleared = false;
              this.search = '';
            }
          } else {
            this.toasterService.error('File upload failed--', '', {
              timeOut: 2000
            });
          }
        });
      } else {
        let itemMasterUpdated = await this.itemMasterServe.updateItemMaster(this.itemMaster);
        if (itemMasterUpdated) {
          this.toasterService.success('Item master Updated successfully', '', {
            timeOut: 2000
          });
          await this.handlesucessData();
          // this.responseLoader = false;
          this.showForm = false;
          this.UOMConversion = new UOMConversionModel();
          this.conversion = new UOMConversionModel();
          this.registerForm.markAsPristine(); // optional - clears dirty flags
          this.registerForm.markAsUntouched(); // optional - clears touched state
          this.itemMaster.conversion = undefined;
          this.itemMaster.stockable = undefined;
          this.itemMaster.isInventory = undefined;
          this.itemMaster.item_type_id = undefined;
          this.itemMaster.culture_category_id = undefined;
          this.itemMaster.item_name = '';
          this.itemMaster.description = '';
          this.itemMaster.uom_id = undefined;
          this.conversion.to_value = undefined;
          this.conversion.to_units_uom_id = undefined;
          this.itemMaster.reorder_level_value = undefined;
          this.itemMaster.reorder_uom_id = undefined;
          this.conversion.from_value = undefined;
          this.conversion.system_defined = undefined;
          this.conversion.id = undefined;
          this.conversion.from_units_uom_id = undefined;
          this.itemMaster.sub_type = undefined;
          this.itemMaster.shape_id = undefined;
          this.itemMaster.pellet_size = undefined;
          this.itemMaster.usage = undefined;
          this.itemMaster.dosage = undefined;
          this.itemMaster.attachment = undefined;
          this.conversionBlock = false;
          this.conversion.to_units_uom_id = undefined;
          this.conversion.from_units_uom_id = undefined;
          this.submitted = false;
          this.imageFile = undefined;
          this.isFileSelected = false;
          this.selectedbase64file = undefined;
          this.fileName = undefined;
          this.addProductDoubleClick = false;
          this.itemMaster.attachment_path = null;
          this.isCleared = false;
          this.search = '';
        }
      }
      this.addProductDoubleClick = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.addProductDoubleClick = false;
    }
  }  // End Update item master functionality

  // get todayDate
  /**
   * Returns the current date and time formatted as a string.
   *
   * @method todayDate
   * @returns {string} Formatted date string (YYYY-MM-DD HH:mm:ss).
   */
  public todayDate() {
    let year = new Date().getFullYear();
    let month = new Date().getMonth() + 1;
    let dateday = new Date().getDate();
    let hours = new Date().getHours();
    let minutes = new Date().getMinutes();
    let seconds = new Date().getSeconds();
    let dateStr = year + '-' + month + '-' + dateday + ' ' + hours + ':' + minutes + ':' + seconds;
    return dateStr;
  }

  // handel handlestockable
  /**
   * Handles the logic when 'Stockable' checkbox is toggled.
   * Sets `isInventory` and `conversionBoolean` flags.
   *
   * @method handlestockable
   * @returns {void}
   */
  public handlestockable() {
    if (this.itemMaster.stockable) {
      this.itemMaster.isInventory = true;
      this.conversionBoolean = true;
    } else {
      this.itemMaster.isInventory = false;
      this.conversionBoolean = false;
    }
  }

  // inventory check
  /**
   * Checks inventory status (placeholder/incomplete logic).
   *
   * @method inventorycheck
   * @returns {void}
   */
  public inventorycheck() {
    if (this.itemMaster.stockable == false) {
      return;
    }
  }

  /**
   * Handles logic when product type changes.
   * Automatically sets stockable/inventory for certain types (e.g., Feed, Medicine).
   *
   * @method productChange
   * @returns {void}
   */
  public productChange() {
    if (this.itemMaster.item_type_id == 1 || this.itemMaster.item_type_id == 2) {
      this.itemMaster.isInventory = true;
      this.itemMaster.stockable = true;
    } else {
      this.itemMaster.isInventory = undefined;
      this.itemMaster.stockable = undefined;
    }
  }

  // Define the method to update the toggle switch state
  /**
   * Flag to disable the toggle switch.
   * @type {boolean}
   * @default false
   */
  public isToggleDisabled: boolean = false;

  /**
   * Updates the state of the toggle switch based on selected item type.
   * Forces 'Stockable' to true for certain types.
   *
   * @method updateToggleSwitchState
   * @returns {void}
   */
  updateToggleSwitchState() {
    const selectedValue = String(this.registerForm.get('itemtype')?.value);
    if (selectedValue === '1' || selectedValue === '2') {
      this.isToggleDisabled = true;
      this.registerForm.get('stockable')?.setValue(true); // Force Yes
    } else {
      this.isToggleDisabled = false;
      this.registerForm.get('stockable')?.setValue(false); // Default No, but user can change
    }

  }

  /**
   * Updates the state of the toggle switch (alternative implementation).
   * Directly manipulates DOM element (not recommended in Angular, prefer `updateToggleSwitchState`).
   *
   * @method updateToggleSwitchState1
   * @returns {void}
   */
  updateToggleSwitchState1() {
    const selectedValue = this.itemMaster.item_type_id;
    // Get a reference to the toggle switch element
    const toggleSwitch = document.getElementById('toggleSwitch') as HTMLInputElement;

    // Check if the selected value should enable or disable the toggle switch
    if ((selectedValue === '1' || selectedValue === '2')) {
      // Enable the toggle switch
      toggleSwitch.disabled = true;
    } else {
      // Disable the toggle switch
      toggleSwitch.disabled = false;
    }
  }

  // onchange uom details means popup
  /**
   * Handles UOM selection changes in the conversion popup.
   * Updates From/To UOM names based on system UOM status.
   *
   * @method onchangeUOM
   * @param {string} value - Context of change ('tablefrom', 'from', 'to').
   * @returns {void}
   */
  public onchangeUOM(value: string) {
    if (value == 'tablefrom') {
      let uom_id = this.itemMaster.uom_id;
      let uomArr = _.filter(this.uoms, function(o) {
        return (o.id as any) == uom_id;
      });
      if (uomArr.length > 0) {
        this.systemuomcondition = uomArr[0].system_uom;
        this.UOMName = uomArr[0].name;
        if (this.systemuomcondition) { // uom selected value == true in the popup touom is not editable
          this.fromUOMName = uomArr[0].name;
          if (this.setFirsttime != true) {
            this.conversion.from_units_uom_id = uom_id;
            this.conversion.to_units_uom_id = null;
          }
        } else {
          this.toUOMName = uomArr[0].name;
          if (this.setFirsttime != true) {
            this.conversion.from_units_uom_id = null;
            this.conversion.to_units_uom_id = uom_id;
          }
        }
      }
      this.setFirsttime = false;
    } else if (value == 'from') {
      let from_units_uom_id = this.conversion.from_units_uom_id;
      let uomArr = _.filter(this.uoms, function(o) {
        return (o.id as any) == from_units_uom_id;
      });
      if (uomArr.length > 0) {
        if (uomArr[0].system_uom) {
          this.fromUOMName = uomArr[0].name;
        } else {
          this.toUOMName = uomArr[0].name;
        }
      }
    } else if (value == 'to') {
      let to_units_uom_id = this.conversion.to_units_uom_id;
      let uomArr = _.filter(this.uoms, function(o) {
        return (o.id as any) == to_units_uom_id;
      });
      if (uomArr.length > 0) {
        if (uomArr[0].system_uom) {
          this.fromUOMName = uomArr[0].name;
        } else {
          this.toUOMName = uomArr[0].name;
        }
      }
    }
  }

  // if without enter the from value or to value display alert message and click on ok button automatically display the pop up of conversion
  /**
   * Opens the conversion popup if UOM and stockable are set.
   *
   * @async
   * @method onclickconversionopen
   * @returns {Promise<void>}
   */
  async onclickconversionopen() {
    if (this.itemMaster.uom_id != undefined && this.itemMaster.stockable != undefined) {
      this.conversionBlock = true;
    }
  }

  // handle success data if add item master is farmer or system releated
  /**
   * Refreshes data after successful add/update operation.
   *
   * @async
   * @method handlesucessData
   * @returns {Promise<void>}
   */
  public async handlesucessData() {
    try {
      this.itemMasters = new Array();
      this.imageFile = undefined;
      this.addProductDoubleClick = false;
      $('#imageUpload').val('');
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.getAllItemMasterbyloginFarmer();
      } else {
        if (this.itemMaster.system == true && this.itemMaster.business_id == '') {
          this.getAllItemMaster();
        } else if (this.itemMaster.system == false && this.itemMaster.business_id != '') {
          this.getAllItemMasterbyFarmer();
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  // reorderValueNumber validation
  /**
   * Validates that the reorder level value is a number.
   *
   * @method reorderValueNumber
   * @returns {void}
   */
  public reorderValueNumber() {
    let value = this.itemMaster.reorder_level_value;
    if ('' + value == 'NaN') {
      this.toasterService.error('Enter only number');
      this.itemMaster.reorder_level_value = undefined;
    } else {
      this.itemMaster.reorder_level_value = value;
    }
  }

  // Accept only two digits after decimal point
  /**
   * Filters input to accept only numbers with up to two decimal places.
   *
   * @method decimalFilter
   * @param {any} event - The keyboard event.
   * @returns {void}
   */
  public decimalFilter(event: any) {
    const reg = /^-?\d*(\.\d{0,2})?$/;
    let input = event.target.value + String.fromCharCode(event.charCode);
    if (!reg.test(input)) {
      event.preventDefault();
    }
  }

  // search functionality
  /**
   * Filters the item master list based on search query.
   *
   * @async
   * @method searchItemMaster
   * @returns {Promise<void>}
   */
  public async searchItemMaster() {
    let value = this.search.toLowerCase();
    $('.searchfun').filter(function() {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }

  /*handel submit and update buttons*/

  /**
   * Opens the form for adding a new product.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Sets button visibility for add mode.
   *
   * @method hideAndShowButtons
   * @returns {void}
   */
  public hideAndShowButtons() {
    this.submitButton = true;
    this.updateButton = false;
  }

  /**
   * Resets the form and component state to default.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.showForm = false;
    this.submitButton = false;
    this.updateButton = false;
    this.registerForm.reset();
    this.addProductDoubleClick = false;
    this.itemMaster.stockable = undefined;
    this.itemMaster.isInventory = undefined;
    this.itemMaster.item_type_id = undefined;
    this.itemMaster.culture_category_id = undefined;
    this.itemMaster.item_name = '';
    this.itemMaster.description = '';
    this.itemMaster.uom_id = undefined;
    this.conversion.to_value = undefined;
    this.conversion.to_units_uom_id = undefined;
    this.itemMaster.reorder_level_value = undefined;
    this.itemMaster.reorder_uom_id = undefined;
    this.conversion.from_value = undefined;
    this.conversion.system_defined = undefined;
    this.conversion.id = undefined;
    this.conversion.from_units_uom_id = undefined;
    this.itemMaster.sub_type = undefined;
    this.itemMaster.shape_id = undefined;
    this.itemMaster.pellet_size = undefined;
    this.itemMaster.usage = undefined;
    this.itemMaster.dosage = undefined;
    this.imageFile = undefined;
    this.isFileSelected = false;
    this.selectedbase64file = undefined;
    this.fileName = undefined;
    this.isCleared = false;
    this.conversionBlock = false;
    this.conversion.to_units_uom_id = undefined;
    this.conversion.from_units_uom_id = undefined;
    this.submitted = false;
    $('#imageUpload').val('');
  }

  /*validations for only number allowed*/
  /**
   * Validates that pellet size is a number.
   *
   * @method pelletSize
   * @returns {void}
   */
  public pelletSize() {
    const value = Number(this.itemMaster.pellet_size);
    if ('' + value == 'NaN') {
      this.toasterService.error('Enter only number');
      this.itemMaster.pellet_size = undefined;
    }
  }

  // file upload
  /**
   * Handles file selection for image upload.
   * Validates file size and reads file as base64.
   *
   * @method fileupload
   * @param {any} event - The file input change event.
   * @returns {void}
   */
  public fileupload(event: any) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 1048576) {
          this.toasterService.warning('Image size should not exceed 1MB');
          $('#imageUpload').val('');
        } else {
          this.imageFile = file;
          this.isFileSelected = true;
          this.fileName = file.name;
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            const dataresult = '' + reader.result;
            this.selectedbase64file = dataresult;
          };
          reader.onerror = () => {

          };
        }
      } else {
        this.isFileSelected = false;
      }
    }
  }

  // Remove selected br  anding image
  /**
   * Removes the selected image file.
   *
   * @method remove
   * @returns {void}
   */
  public remove() {
    const deleteimage = confirm('Are you sure you want to delete these image?');
    if (deleteimage) {
      this.imageFile = undefined;
      this.isFileSelected = false;
      this.fileName = undefined;
      this.isCleared = true;
      this.selectedbase64file = undefined;
      $('#imageUpload').val('');
    }
  }

  /*item selection conditions*/
  /**
   * Handles business selection via keyboard (Enter key).
   *
   * @method selectEventOnKeyBased
   * @param {any} item - The selected item.
   * @param {KeyboardEvent} event - The keyboard event.
   * @returns {void}
   */
  public selectEventOnKeyBased(item: any, event: KeyboardEvent) {
    if (event.key == 'Enter') {
      event.preventDefault();
      this.itemMaster.business_id = item.id;
    }
    if (this.itemMaster.business_id && (this.itemMaster.business_id != '')) {
      this.gettabledatabyfarm();
    }
  }

  /**
   * Selected business name for filtering.
   * @type {string}
   */
  public selectedBusinessName: string = '';

  /**
   * Filters business options based on search text.
   *
   * @method filterBusinesses
   * @returns {void}
   * @side-effect Updates filteredOptions array.
   */
  public filterBusinesses(): void {
    const searchText = this.selectedBusinessName.toLowerCase();
    this.filteredOptions = this.businessEntities.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * Handles business selection from dropdown.
   *
   * @method selectOption
   * @param {BusinessEntity} business - The selected business entity.
   * @returns {void}
   */
  public selectOption(business: BusinessEntity) {
    this.selectedBusinessName = business.businessName;
    const selected = this.businessEntities.find(b => b.businessName === this.selectedBusinessName);
    this.itemMaster.business_id = selected.id;
    this.businessName = selected.businessName;
    if (this.itemMaster.business_id) {
      this.gettabledatabyfarm();
    }
  }
}
